/** API e stato server del modulo mercati (SPEC-010). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type MarketStatus =
  | 'open'
  | 'closed'
  | 'pre_market'
  | 'after_hours'
  | 'lunch_break'
  | 'opening_auction'
  | 'closing_auction'
  | 'holiday'
  | 'weekend';

export type MarketListItem = {
  mic: string;
  name: string;
  city: string;
  countryCode: string;
  currency: string;
  timezoneIana: string;
  status: MarketStatus;
  /** `null` per un mercato sempre aperto: non c'è un prossimo evento da mostrare. */
  nextOpen: string | null;
  nextClose: string | null;
  /** Mercato sempre aperto (criptovalute): niente weekend, niente festività, nessuna apertura. */
  trades24x7: boolean;
  /** Ora locale del resoconto giornaliero ("HH:mm"), per i mercati che ne hanno una fissa. */
  briefingTimeLocal: string | null;
  /** Titoli della watchlist dell'utente collegati a questo mercato. */
  watchlistInstrumentCount: number;
  triggerEnabled: boolean;
  /** Revisione di fine seduta continua (SPEC-017), riservata ai premium. */
  preCloseTriggerEnabled: boolean;
  /** Revisione di fase after-hours (SPEC-017), riservata ai premium. */
  afterHoursTriggerEnabled: boolean;
  /**
   * F-144: come gira ciascuna revisione di fine giornata. `off` coincide con il
   * flag spento; `agentic` e' lo studio con gli strumenti (SPEC-025).
   */
  preCloseMode: EndOfDayMode;
  afterHoursMode: EndOfDayMode;
  /**
   * F-199: come gira il resoconto pre-apertura. Indipendente dal flag
   * `triggerEnabled`, e vale anche per il resoconto chiesto a mano.
   */
  openMode: OpenMode;
  /** SPEC-026 §7.3: su un mercato 24/7 la revisione di fine giornata scatta a quest'ora locale (`HH:MM`). */
  reviewTimeLocal: string | null;
  /** La borsa negozia in after-hours: senza, l'interruttore sarebbe sempre rifiutato dal server. */
  supportsAfterHours: boolean;
};

export type EndOfDayMode = 'off' | 'standard' | 'agentic';

/**
 * F-199: `standard` = TradingAgents su tutta la watchlist del mercato, poi il
 * riassunto; `agentic_deferred` = uno studio agentico subito, che chiede al
 * massimo 3 analisi e riprende una volta quando sono finite.
 */
export type OpenMode = 'standard' | 'agentic_deferred';

/** F-199: una richiesta di analisi dello studio, come la registra il resoconto. */
export type DeferredAnalysisRequest = {
  slot: number;
  instrumentId: string;
  symbol: string | null;
  outcome: 'requesting' | 'requested' | 'reused' | 'quota_exceeded' | 'failed';
  analysisStatus: 'pending' | 'running' | 'completed' | 'failed' | null;
  reason: string;
};

export type MarketSchedule = {
  continuousOpen: string;
  continuousClose: string;
  lunchStart: string | null;
  lunchEnd: string | null;
  preMarketOpen: string | null;
  afterHoursClose: string | null;
  openingAuctionStart: string | null;
  openingAuctionEnd: string | null;
  closingAuctionStart: string | null;
  closingAuctionEnd: string | null;
  notes: string | null;
};

export type WatchlistInstrumentOnMarket = {
  instrumentId: string;
  name: string;
  symbol: string | null;
  isin: string | null;
};

export type BriefingStatus = 'collecting' | 'summarizing' | 'completed' | 'failed';

export type BriefingSummary = {
  id: string;
  briefingDate: string;
  status: BriefingStatus;
  instrumentCount: number;
  analysesCompletedCount: number;
  analysesFailedCount: number;
  analysesSkippedCount: number;
  sentToTelegramAt: string | null;
  createdAt: string;
  /** `open` al mattino; `pre_close` / `after_hours` per le revisioni di fine giornata. */
  kind: string;
  /** F-144: `agentic` quando la revisione e' uno studio con gli strumenti. */
  mode: 'standard' | 'agentic';
};

export type BriefingAnalysis = {
  analysisId: string;
  instrumentId: string;
  instrumentName: string;
  rating: string | null;
  executiveSummary: string | null;
  priceTarget: number | null;
  timeHorizon: string | null;
};

/** Che cosa è partito da questo resoconto (TODO.md). */
export type BriefingChain = {
  autoTradingRequested: boolean;
  analysesTriggered: number;
  analysesCompleted: number;
  analysesFailed: number;
  digest: { id: string; status: string } | null;
  autoTradeRun: { id: string; status: string; executedCount: number; rejectedCount: number } | null;
  /** Le singole proposte del giorno: un conteggio non dice dove guardare. */
  proposals: BriefingProposal[];
};

export type BriefingProposal = {
  id: string;
  instrumentId: string;
  symbol: string | null;
  side: string;
  status: string;
  rejectionReasonCode: string | null;
};

export type BriefingInstrument = {
  instrumentId: string;
  name: string;
  symbol: string | null;
  analysisId: string | null;
  rating: string | null;
  priceTarget: number | null;
  /** Prezzo noto all'istante del resoconto, non quello di adesso. */
  priceAtBriefing: string | null;
  priceCurrency: string | null;
  priceAt: string | null;
  inWishlist: boolean;
};

export type BriefingDetail = BriefingSummary & {
  chain: BriefingChain;
  /** Strumenti anche in wishlist, per ID: un simbolo può ripetersi su mercati diversi. */
  wishlistInstrumentIds: string[];
  /** I titoli entrati nel resoconto, con l'analisi da cui il giudizio viene. */
  instruments: BriefingInstrument[];
  summaryMarkdown: string | null;
  fullReport: {
    generatedByLlm: boolean;
    /** Valorizzato solo se il briefing è vuoto: distingue "quota esaurita" da un guasto dell'LLM. */
    emptyReason?: 'quota_esaurita' | 'analisi_fallite' | 'nessuna_analisi_pronta' | null;
    analyses: BriefingAnalysis[];
    /** F-144: presenti solo per una revisione agentica. */
    mode?: 'standard' | 'agentic';
    agentRunId?: string | null;
    runStatus?: string | null;
    terminatedReason?: string | null;
    proposalsSubmitted?: number;
    gateBlocks?: number;
    costUsd?: string | null;
    /** F-199: presenti solo per un resoconto pre-apertura con analisi differita. */
    variant?: 'deferred_analyses';
    firstRunId?: string | null;
    resumeRunId?: string | null;
    analysisRequests?: DeferredAnalysisRequest[];
    /** La ripresa non ha concluso: il testo è il riassunto standard delle analisi. */
    fallback?: 'standard_summary' | null;
  } | null;
};

export type MarketDetail = MarketListItem & {
  schedule: MarketSchedule;
  watchlistInstruments: WatchlistInstrumentOnMarket[];
  recentBriefings: BriefingSummary[];
  triggerFeatureEnabled: boolean;
};

// ---------------------------------------------------------------- Timeline

export type TimelinePhase =
  | 'pre_market'
  | 'opening_auction'
  | 'open'
  | 'lunch_break'
  | 'closing_auction'
  | 'after_hours';

export type TimelineSegment = {
  phase: TimelinePhase;
  start: string;
  end: string;
  sessionDate: string;
  earlyClose: boolean;
};

/**
 * A che cosa si riferisce un evento. Il backend dice il **bersaglio**, non
 * l'URL: i percorsi del router vivono qui (ADR-0005), e riceverli dal server
 * creerebbe una seconda verità che si rompe alla prima rinomina di una rotta.
 */
export type TimelineEventLink =
  | { target: 'briefing'; id: string }
  | { target: 'analysis'; id: string; instrumentId: string }
  | { target: 'autoTrading' };

export type TimelineEvent = {
  id: string;
  at: string;
  kind: 'trigger' | 'briefing' | 'analysis' | 'proposal' | 'order';
  title: string;
  detail: string | null;
  tone: 'neutral' | 'accent' | 'positive' | 'negative' | 'warning';
  /** Calcolato dal server: dipende dallo stesso orologio che definisce la finestra. */
  future: boolean;
  link: TimelineEventLink | null;
};

export type MarketTimeline = {
  mic: string;
  timezoneIana: string;
  windowStart: string;
  windowEnd: string;
  now: string;
  segments: TimelineSegment[];
  events: TimelineEvent[];
  triggerActive: boolean;
};

export type InstrumentMarket = {
  instrumentId: string;
  marketMic: string | null;
  marketResolutionStatus: 'resolved' | 'manual' | 'ambiguous' | 'unresolved';
  marketResolutionCandidates: unknown | null;
  marketResolvedAt: string | null;
  /**
   * Perche' il tentativo non ha prodotto un mercato. `null` quando ne ha
   * prodotto uno: un esito riuscito non ha niente da giustificare.
   */
  unresolvedReason: string | null;
};

export const marketsKeys = {
  list: ['markets', 'list'] as const,
  detail: (mic: string) => ['markets', 'detail', mic] as const,
  briefings: (mic: string) => ['markets', 'briefings', mic] as const,
  briefingDetail: (mic: string, id: string) => ['markets', 'briefing-detail', mic, id] as const,
  timeline: (mic: string) => ['markets', 'timeline', mic] as const,
};

export function useMarkets() {
  return useQuery({
    queryKey: marketsKeys.list,
    // `triggerFeatureEnabled` è il flag globale MARKET_OPEN_TRIGGER_ENABLED:
    // senza, il toggle per-mercato scrive lo stato ma il job che lo legge non
    // gira affatto — la UI lo usa per disattivare/spiegare l'interruttore
    // invece di lasciarlo sembrare attivo senza esserlo davvero.
    queryFn: ({ signal }) => api.get<{ items: MarketListItem[]; triggerFeatureEnabled: boolean }>('/markets', signal),
  });
}

export function useMarketDetail(mic: string, enabled = true) {
  return useQuery({
    queryKey: marketsKeys.detail(mic),
    queryFn: ({ signal }) => api.get<MarketDetail>(`/markets/${mic}`, signal),
    enabled,
  });
}

/**
 * Linea del tempo del mercato (TODO.md).
 *
 * `refetchInterval` di un minuto: la barra contiene un marcatore «adesso» e
 * degli istanti futuri, quindi invecchia da sola anche senza che cambi nulla
 * sul server. Un minuto è la granularità con cui la barra è leggibile — più
 * spesso non si vedrebbe, meno spesso il marcatore resterebbe indietro in modo
 * percepibile.
 */
export function useMarketTimeline(mic: string, enabled = true) {
  return useQuery({
    queryKey: marketsKeys.timeline(mic),
    queryFn: ({ signal }) => api.get<MarketTimeline>(`/markets/${mic}/timeline`, signal),
    enabled,
    refetchInterval: 60_000,
  });
}

/**
 * I tre inneschi si aggiornano **uno alla volta**: il payload porta solo quello
 * toccato, e il server lascia gli altri come sono. Mandarli tutti e tre
 * significherebbe riscrivere uno stato che la pagina potrebbe aver letto prima
 * di un cambiamento avvenuto altrove.
 */
export type TriggerPatch = {
  enabled?: boolean;
  preCloseEnabled?: boolean;
  afterHoursEnabled?: boolean;
  /** F-144: la modalita' porta con se' il flag (`off` lo spegne, le altre lo accendono). */
  preCloseMode?: EndOfDayMode;
  afterHoursMode?: EndOfDayMode;
  /** F-199: la modalita' del resoconto pre-apertura; non tocca `enabled`. */
  openMode?: OpenMode;
  /** SPEC-026 §7.3: l'ora della revisione serale sui mercati 24/7; `null` la toglie. */
  reviewTimeLocal?: string | null;
};

export function useSetMarketTrigger(mic: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: TriggerPatch) =>
      api.patch<{ mic: string; enabled: boolean }>(`/markets/${mic}/trigger`, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketsKeys.list });
      void queryClient.invalidateQueries({ queryKey: marketsKeys.detail(mic) });
      // La barra mostra gli inneschi futuri: spegnere il trigger deve toglierli
      // subito, altrimenti resta a indicare un appuntamento annullato.
      void queryClient.invalidateQueries({ queryKey: marketsKeys.timeline(mic) });
    },
  });
}

/**
 * Resoconto di mercato "adesso", senza aspettare la finestra automatica
 * (TODO.md). Riservato agli utenti premium e conteggiato sulla quota generale
 * delle analisi manuali: il backend rifiuta con 403 chi non è abilitato.
 */
export type BriefingRequestResult = BriefingSummary & {
  /** `false` = il resoconto di oggi esisteva già: NON è stato avviato nulla. */
  created: boolean;
  /** Esisteva già ed è concluso: non passerà più dal punto che rilegge l'intenzione. */
  alreadyTerminal: boolean;
  autoTradingRequested: boolean;
  autoTradingScheduled: boolean;
  /** Codice strutturato quando la spunta non è stata onorata (SPEC-014.2 §A.3). */
  autoTradingReason: string | null;
};

export function useRequestBriefing(mic: string) {
  const queryClient = useQueryClient();
  // La variabile è dichiarata esplicitamente: con un parametro dotato di
  // default, `useMutation` inferisce `void` e i chiamanti non possono più
  // passare il flag.
  return useMutation<BriefingRequestResult, Error, { runAutoTrading: boolean; force?: boolean }>({
    // Il flag è FACOLTATIVO e falso per difetto (SPEC-014.2 §A.3): la risposta
    // dichiara se l'auto-trading è stato programmato e, se no, perché.
    mutationFn: ({ runAutoTrading, force }) =>
      api.post<BriefingRequestResult>(`/markets/${mic}/briefings/request`, { runAutoTrading, force }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketsKeys.detail(mic) });
      void queryClient.invalidateQueries({ queryKey: marketsKeys.briefings(mic) });
      void queryClient.invalidateQueries({ queryKey: ['reports', 'list'] });
    },
  });
}

export function useMarketBriefings(mic: string, enabled = true) {
  return useQuery({
    queryKey: marketsKeys.briefings(mic),
    queryFn: ({ signal }) => api.get<{ items: BriefingSummary[] }>(`/markets/${mic}/briefings`, signal),
    enabled,
  });
}

export function useBriefingDetail(mic: string, briefingId: string, enabled: boolean) {
  return useQuery({
    queryKey: marketsKeys.briefingDetail(mic, briefingId),
    queryFn: ({ signal }) => api.get<BriefingDetail>(`/markets/${mic}/briefings/${briefingId}`, signal),
    enabled,
  });
}

export function useResolveInstrumentMarket(instrumentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<InstrumentMarket>(`/instruments/${instrumentId}/market/resolve`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketsKeys.list });
      void queryClient.invalidateQueries({ queryKey: ['watchlist', 'list'] });
    },
  });
}

export function useSetInstrumentMarket(instrumentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mic: string) => api.patch<InstrumentMarket>(`/instruments/${instrumentId}/market`, { mic }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketsKeys.list });
      void queryClient.invalidateQueries({ queryKey: ['watchlist', 'list'] });
    },
  });
}
